import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Mail, ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';
import { apiClient } from '../../services/api.client';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post<{ message: string }>('/v2/auth/forgot-password', { email });
      
      setEmailSent(true);
      setSuccess(response.message || 'If an account exists with this email, you will receive password reset instructions shortly.');
      
      // Navigate back to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-2xl mb-4 shadow-accent-glow">
            <Camera size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">Vision 24</h1>
          <p className="text-text-muted">Smart Security Solutions</p>
        </div>

        {/* Forgot Password card */}
        <div className="bg-surface-primary rounded-card shadow-card border border-surface-secondary p-8">
          {/* Back button */}
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-accent-primary hover:text-accent-secondary transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back to Sign In</span>
          </button>

          <h2 className="text-2xl font-semibold text-text-primary mb-2 text-center">
            Forgot Password?
          </h2>
          <p className="text-text-muted text-center mb-6">
            Enter your email address and we'll send you instructions to reset your password
          </p>

          {error && (
            <div className="mb-6">
              <Alert variant="error" message={error} onClose={() => setError(null)} />
            </div>
          )}

          {success && (
            <div className="mb-6">
              <Alert variant="success" message={success} />
            </div>
          )}

          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                name="email"
                type="email"
                label="Email Address"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftIcon={<Mail size={18} />}
                fullWidth
                autoComplete="email"
                disabled={isLoading}
                autoFocus
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading || !email}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">Check your email</h3>
              <p className="text-text-muted text-sm">
                If an account exists with this email, you will receive password reset instructions shortly.
              </p>
              <p className="text-text-muted text-sm mt-4">
                Redirecting to login...
              </p>
            </div>
          )}

          {!emailSent && (
            <div className="mt-6 text-center">
              <p className="text-sm text-text-muted">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-accent-primary hover:text-accent-secondary font-medium transition-colors"
                >
                  Sign In
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-text-muted">
          © 2026 Vision 24. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
